import React, { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { StagesByWorkflowDocument } from "#app/lib/data";
import type { EntityItemConfig, EntityPoolConfig } from "@salestrak/ui";
import { EntityTag } from "#app/components/entity";
import type { Omittable } from "@salestrak/ui";

const POOL_ID = "stages";

export type StagePoolStage = Omittable<{
  stageId: string;
  name: string;
  type?: string | null;
  icon?: { color?: string | null; percentage?: number | null } | null;
  sortOrder?: number | null;
  isDefault?: boolean | null;
}>;

// THE stage → selector-item mapping. Every selector/filter that lists stages
// goes through this so the glyph treatment (EntityTag visual) can't drift per
// call site. Glyph size matches icon kinds via EntityVisual's stage branch.
export function stageToPoolItem(
  node: StagePoolStage,
  description?: string,
): EntityItemConfig {
  const stage = {
    type: node.type ?? null,
    icon: {
      color: node.icon?.color ?? null,
      percentage: node.icon?.percentage ?? 0,
    },
  };
  return {
    id: node.stageId,
    title: node.name,
    description,
    avatar: {
      render: () =>
        React.createElement(EntityTag, {
          entity: { kind: "stage", id: node.stageId, stage },
          display: "visual",
          size: "sm",
          interactive: false,
        }),
    },
    data: {
      type: node.type,
      sortOrder: node.sortOrder,
      isDefault: node.isDefault,
      icon: stage.icon,
    },
  } satisfies EntityItemConfig;
}

/**
 * Fetches stages for a given workflow and returns an EntityPoolConfig.
 */
export function useStagePool(workflowId: string | null | undefined) {
  const variables = useMemo(() => ({ workflowId: workflowId! }), [workflowId]);
  const { data, loading } = useQuery(StagesByWorkflowDocument, {
    variables,
    skip: !workflowId,
  });

  const items = useMemo<EntityItemConfig[]>(() => {
    if (!data?.stages?.edges) return [];
    return data.stages.edges
      .filter((edge) => edge?.node)
      .map((edge) => stageToPoolItem(edge.node))
      .sort((a, b) => ((a.data?.sortOrder as number) ?? 0) - ((b.data?.sortOrder as number) ?? 0));
  }, [data]);

  const pool = useMemo<EntityPoolConfig>(
    () => ({
      id: POOL_ID,
      label: "Stages",
      items,
    }),
    [items],
  );

  return { pool, loading };
}

export { POOL_ID as STAGE_POOL_ID };
